"use client"

import { useState } from "react"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import axios from "axios"
import { API_URL } from "../config"
import "./Contact.css"

const Contact = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  })
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await axios.post(`${API_URL}/api/contact`, formData)
      showNotification(t("Message sent successfully! We'll get back to you soon."), "success")
      setFormData({ name: "", email: "", subject: "", message: "" })
    } catch (err) {
      showNotification(t("Failed to send message. Please try again."), "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="contact-container" dir={direction}>
      <div className="contact-hero">
        <div className="hero-content">
          <h1>{t("Contact Us")}</h1>
          <p>{t("Get in touch with our team for support, questions, or feedback")}</p>
        </div>
      </div>

      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>{t("Get in Touch")}</h2>
              <p>
                {t(
                  "We're here to help you with any questions about WATref Calculator. Reach out to us through any of the following channels:",
                )}
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="method-info">
                    <h3>{t("Email")}</h3>
                    <p>support@etrefcalculator.com</p>
                    <p>info@etrefcalculator.com</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <i className="fas fa-phone"></i>
                  </div>
                  <div className="method-info">
                    <h3>{t("Phone")}</h3>
                    <p>+1 (555) 123-4567</p>
                    <p>{t("Mon-Fri, 9AM-6PM EST")}</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="method-info">
                    <h3>{t("Address")}</h3>
                    <p>123 Agriculture Tech Blvd</p>
                    <p>Suite 456, Tech City, TC 12345</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="method-info">
                    <h3>{t("Business Hours")}</h3>
                    <p>{t("Monday - Friday: 9:00 AM - 6:00 PM")}</p>
                    <p>{t("Saturday: 10:00 AM - 4:00 PM")}</p>
                    <p>{t("Sunday: Closed")}</p>
                  </div>
                </div>
              </div>

              <div className="social-links">
                <h3>{t("Follow Us")}</h3>
                <div className="social-icons">
                  <a href="#" className="social-icon">
                    <i className="fab fa-facebook"></i>
                  </a>
                  <a href="#" className="social-icon">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href="#" className="social-icon">
                    <i className="fab fa-linkedin"></i>
                  </a>
                  <a href="#" className="social-icon">
                    <i className="fab fa-youtube"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-form-section">
              <div className="form-card">
                <h2>{t("Send us a Message")}</h2>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">{t("Name")} *</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">{t("Email")} *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject">{t("Subject")} *</label>
                    <select id="subject" name="subject" value={formData.subject} onChange={handleChange} required>
                      <option value="">{t("Select a subject")}</option>
                      <option value="general">{t("General Inquiry")}</option>
                      <option value="support">{t("Technical Support")}</option>
                      <option value="billing">{t("Billing Question")}</option>
                      <option value="feature">{t("Feature Request")}</option>
                      <option value="bug">{t("Bug Report")}</option>
                      <option value="partnership">{t("Partnership")}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">{t("Message")} *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      placeholder={t("Please describe your inquiry in detail...")}
                      required
                    ></textarea>
                  </div>

                  <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? t("Sending...") : t("Send Message")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="faq-section">
        <div className="container">
          <h2>{t("Frequently Asked Questions")}</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>{t("How accurate are the WATref calculations?")}</h3>
              <p>
                {t(
                  "Our calculations are based on the FAO-56 Penman-Monteith equation, which is the international standard for reference evapotranspiration calculations with accuracy rates above 95%.",
                )}
              </p>
            </div>
            <div className="faq-item">
              <h3>{t("Can I use this for any crop type?")}</h3>
              <p>
                {t(
                  "Yes, our platform supports a wide variety of crops with specific crop coefficients. You can also create custom crop profiles for specialized varieties.",
                )}
              </p>
            </div>
            <div className="faq-item">
              <h3>{t("Is weather data updated in real-time?")}</h3>
              <p>
                {t(
                  "We integrate with multiple weather services to provide real-time meteorological data, updated every hour for the most accurate calculations.",
                )}
              </p>
            </div>
            <div className="faq-item">
              <h3>{t("Do you offer training or support?")}</h3>
              <p>
                {t(
                  "Yes, we provide comprehensive training materials, video tutorials, and dedicated support to help you get the most out of our platform.",
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
